import { FileText, Download, ExternalLink } from "lucide-react";
import { fileUrl } from "@/lib/api";

/**
 * Renders a list of project attachments. Image attachments are shown in a
 * responsive grid, while non-image attachments (PDFs etc.) render as cards
 * with an icon + filename and an open/download link.
 */
export default function AttachmentList({ attachments = [], onRemove, testidPrefix }) {
  if (!attachments || attachments.length === 0) return null;
  const images = attachments.filter((a) => (a.content_type || "").startsWith("image/"));
  const docs = attachments.filter((a) => !(a.content_type || "").startsWith("image/"));

  return (
    <div className="space-y-5">
      {images.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {images.map((a, i) => (
            <div key={a.file_id} className="relative">
              <img
                src={fileUrl(a.file_id)}
                alt={a.filename || ""}
                className="w-full rounded-2xl object-cover"
              />
              {onRemove && (
                <button
                  onClick={() => onRemove(a.file_id)}
                  type="button"
                  className="absolute right-2 top-2 rounded-full bg-stone-900/80 px-2 py-1 text-xs text-white hover:bg-stone-900"
                  data-testid={testidPrefix ? `${testidPrefix}-remove-${i}` : undefined}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {docs.length > 0 && (
        <div className="space-y-5">
          {docs.map((a, i) => (
            <div key={a.file_id}>
              <div
                className="group flex items-center gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm transition hover:border-amber-300 hover:bg-amber-50/40"
                data-testid={testidPrefix ? `${testidPrefix}-pdf-${i}` : undefined}
              >
                <div className="rounded-xl bg-red-50 p-3 ring-1 ring-red-100">
                  <FileText className="h-6 w-6 text-red-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-stone-900">
                    {a.filename || "Document"}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-stone-500">
                    {(a.content_type || "application/pdf").split("/")[1] || "pdf"}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <a
                    href={fileUrl(a.file_id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-2.5 py-1 text-xs font-medium text-stone-800 hover:bg-stone-50"
                    title="Open"
                    data-testid={testidPrefix ? `${testidPrefix}-open-${i}` : undefined}
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> Open
                  </a>
                  <a
                    href={fileUrl(a.file_id)}
                    download={a.filename || "document.pdf"}
                    className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-2.5 py-1 text-xs font-medium text-stone-800 hover:bg-stone-50"
                    title="Download"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                  {onRemove && (
                    <button
                      onClick={() => onRemove(a.file_id)}
                      type="button"
                      className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <iframe
                title={a.filename || "PDF preview"}
                src={fileUrl(a.file_id)}
                data-testid={testidPrefix ? `${testidPrefix}-preview-iframe-${i}` : undefined}
                className="mt-2 h-[420px] w-full rounded-2xl border border-stone-200 bg-stone-50"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
